import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
} from 'react';
import {useSelector} from 'react-redux';
import {selectUserInfo} from '../store/user';
import UpgradeModal from '../components/UpgradeModal';
import {
  fetchCurriculumSetupAlert,
  HandleCurriculumSetupAlertParams,
  fetchNextGradeInfo,
} from '../API/newHome';
import Toast from 'react-native-toast-message';
import SubjectVersionModal from '../components/SubjectVersionModal';

interface GradeUpgradeContextType {
  shouldUpgrade: boolean;
  upgradeType?: 'stage' | 'grade' | 'term';
  checkTime: string | null;
  nextGrade?: number;
  checkNeedUpgrade: () => Promise<{
    needUpgrade: boolean;
    type?: 'stage' | 'grade' | 'term';
    alertDate?: string;
    nextGrade?: number;
  }>;
  handleUpgrade: () => void;
  handleSkipUpgrade: () => void;
  testUpgrade?: (type: 'stage' | 'grade' | 'term') => Promise<void>;
}

const GradeUpgradeContext = createContext<GradeUpgradeContextType>({
  shouldUpgrade: false,
  upgradeType: undefined,
  checkTime: null,
  nextGrade: undefined,
  checkNeedUpgrade: async () => ({needUpgrade: false}),
  handleUpgrade: () => {},
  handleSkipUpgrade: () => {},
});

export const useGradeUpgrade = () => useContext(GradeUpgradeContext);

// 修改测试数据类型
let testUpgradeData: {
  type: 'stage' | 'grade' | 'term';
  alertDate: string;
} | null = null;

export const GradeUpgradeProvider: React.FC<{children: React.ReactNode}> = ({
  children,
}) => {
  const [upgradeInfo, setUpgradeInfo] = useState<
    Omit<
      GradeUpgradeContextType,
      'checkNeedUpgrade' | 'handleUpgrade' | 'handleSkipUpgrade' | 'testUpgrade'
    >
  >({
    shouldUpgrade: false,
    upgradeType: undefined,
    checkTime: null,
    nextGrade: undefined,
  });

  const [showModal, setShowModal] = useState(false);
  const [showSubjectVersionModal, setShowSubjectVersionModal] = useState(false);

  const userInfo = useSelector(selectUserInfo);
  const curriculumSetup = userInfo.curriculumSetup;

  const checkNeedUpgrade = useCallback(async () => {
    if (!curriculumSetup) {
      return {needUpgrade: false};
    }

    const currentDate = new Date();
    const {alertDateOfStageReset, alertDateOfGradeReset, alertDateOfTermReset} =
      curriculumSetup;

    // 1. 优先检查升学段时间
    if (alertDateOfStageReset) {
      const stageDate = new Date(alertDateOfStageReset);
      if (currentDate > stageDate) {
        try {
          const response = await fetchNextGradeInfo();
          const nextGradeInfo = response.data;

          return {
            needUpgrade: true,
            type: 'stage' as const,
            alertDate: alertDateOfStageReset,
            nextGrade: nextGradeInfo.ordinalInAllStage + 1,
            needUpdateSubjects: true,
          };
        } catch (error) {
          console.error('获取下一年级信息失败:', error);
          return {needUpgrade: false};
        }
      }
    }

    // 2. 其次检查升年级时间
    if (alertDateOfGradeReset) {
      const gradeDate = new Date(alertDateOfGradeReset);
      if (currentDate > gradeDate) {
        try {
          const response = await fetchNextGradeInfo();
          const nextGradeInfo = response.data;

          return {
            needUpgrade: true,
            type: 'grade' as const,
            alertDate: alertDateOfGradeReset,
            nextGrade: nextGradeInfo.ordinalInAllStage + 1,
            needUpdateSubjects: false,
          };
        } catch (error) {
          console.error('获取下一年级信息失败:', error);
          return {needUpgrade: false};
        }
      }
    }

    // 3. 最后检查升学期时间
    if (alertDateOfTermReset) {
      const termDate = new Date(alertDateOfTermReset);
      if (currentDate > termDate) {
        return {
          needUpgrade: true,
          type: 'term' as const,
          alertDate: alertDateOfTermReset,
          needUpdateSubjects: false,
        };
      }
    }

    return {needUpgrade: false};
  }, [curriculumSetup]);

  const handleCurriculumSetupAlert = useCallback(
    async (type: 'stage' | 'grade' | 'term', handle: 'keep' | 'next') => {
      let p: HandleCurriculumSetupAlertParams | undefined;
      if (handle === 'next') {
        if (type === 'term') {
          p = {handleType: 'nextTerm'};
        } else if (type === 'grade' || type === 'stage') {
          p = {handleType: 'nextGrade'};
        }
      } else if (handle === 'keep') {
        if (type === 'term') {
          p = {handleType: 'keepTerm'};
        } else if (type === 'grade' || type === 'stage') {
          p = {handleType: 'keepGrade'};
        }
      }

      try {
        console.log('发送升级请求:', p);
        const res = await fetchCurriculumSetupAlert(p);
        if (res.status === 200) {
          setUpgradeInfo(prev => ({
            ...prev,
            shouldUpgrade: false,
          }));
          if (handle === 'next') {
            Toast.show({
              type: 'success',
              text1: '升级成功',
            });
          }
        } else {
          Toast.show({
            type: 'error',
            text1: res.msg || '操作失败',
          });
        }
      } catch (error) {
        console.error('操作失败:', error);
        Toast.show({
          type: 'error',
          text1: '操作失败，请重试',
        });
      }
    },
    [],
  );

  const handleUpgrade = useCallback(() => {
    console.log('执行升级操作', upgradeInfo);
    setShowModal(false);

    if (upgradeInfo.upgradeType) {
      if (upgradeInfo.upgradeType === 'stage') {
        // 如果是升学段，显示科目版本选择弹窗
        setShowSubjectVersionModal(true);
      } else {
        // 其他情况直接处理升级
        handleCurriculumSetupAlert(upgradeInfo.upgradeType, 'next');
      }
    }
  }, [upgradeInfo, handleCurriculumSetupAlert]);

  const handleSkipUpgrade = useCallback(() => {
    setShowModal(false);
    if (upgradeInfo.upgradeType) {
      handleCurriculumSetupAlert(upgradeInfo.upgradeType, 'keep');
    }
  }, [handleCurriculumSetupAlert, upgradeInfo.upgradeType]);

  const testUpgrade = useCallback(async (type: 'stage' | 'grade' | 'term') => {
    if (type === 'grade' || type === 'stage') {
      try {
        const response = await fetchNextGradeInfo();
        const nextGradeInfo = response.data;

        setUpgradeInfo({
          shouldUpgrade: true,
          upgradeType: type,
          checkTime: new Date().toISOString(),
          nextGrade: nextGradeInfo.ordinalInAllStage + 1,
        });
      } catch (error) {
        console.error('获取下一年级信息失败:', error);
        return;
      }
    } else {
      setUpgradeInfo({
        shouldUpgrade: true,
        upgradeType: type,
        checkTime: new Date().toISOString(),
      });
    }
    setShowModal(true);
  }, []);

  const contextValue = useMemo(
    () => ({
      ...upgradeInfo,
      checkNeedUpgrade,
      handleUpgrade,
      handleSkipUpgrade,
      testUpgrade,
    }),
    [
      upgradeInfo,
      checkNeedUpgrade,
      handleUpgrade,
      handleSkipUpgrade,
      testUpgrade,
    ],
  );

  useEffect(() => {
    const autoCheck = async () => {
      const result = await checkNeedUpgrade();
      if (result.needUpgrade && result.type) {
        setUpgradeInfo({
          shouldUpgrade: true,
          upgradeType: result.type,
          checkTime: result.alertDate || null,
          nextGrade: result.nextGrade,
        });
        setShowModal(true);
      }
    };

    autoCheck();
  }, [checkNeedUpgrade]);

  const handleSubjectVersionConfirm = useCallback((curriculumSetup: any) => {
    setShowSubjectVersionModal(false);
    // 直接调用 API 更新课程设置
    fetchCurriculumSetupAlert({
      handleType: 'nextGrade',
      ...curriculumSetup,
    });
  }, []);

  return (
    <GradeUpgradeContext.Provider value={contextValue}>
      {children}
      {showModal && upgradeInfo.upgradeType && (
        <UpgradeModal
          visible={showModal}
          type={upgradeInfo.upgradeType}
          nextGrade={upgradeInfo.nextGrade}
          onConfirm={handleUpgrade}
          onCancel={handleSkipUpgrade}
        />
      )}
      {showSubjectVersionModal && (
        <SubjectVersionModal
          visible={showSubjectVersionModal}
          nextGrade={upgradeInfo.nextGrade}
          onConfirm={handleSubjectVersionConfirm}
          onCancel={() => setShowSubjectVersionModal(false)}
        />
      )}
    </GradeUpgradeContext.Provider>
  );
};

// 修改测试数据设置方法
export const setTestUpgradeData = (type: 'stage' | 'grade' | 'term') => {
  const mockDate = new Date();
  mockDate.setDate(mockDate.getDate() - 1);
  testUpgradeData = {
    type,
    alertDate: mockDate.toISOString(),
  };
};
